import React from 'react';

import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import DeleteIcon from '@mui/icons-material/Delete';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import SleepIcon from '@mui/icons-material/Hotel';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';

import ConfirmDelete from './ConfirmDelete';

import type { HealthRecordTypeType } from '@/types/domain/class/valueObjects/healthRecordType';
import type { DefecationJson } from '@/types/domain/defecation/entities/defecationEntity';
import type { DrugJson } from '@/types/domain/drug/entities/drugEntity';
import type { MealJson } from '@/types/domain/meal/entities/mealEntity';
import type { MilkJson } from '@/types/domain/milk/entities/milkEntity';
import type { SleepJson } from '@/types/domain/sleep/entities/sleepEntity';
import type { TemperatureJson } from '@/types/domain/temperature/entities/temperatureEntity';

import { diffHoursMinutes, getDateString, toStringTime } from '@/utils/date';

/**
 * 健康記録Json
 */
export type HealthRecords = {
  /** 体温 */
  temperatures: TemperatureJson[],
  /** 睡眠 */
  sleeps: SleepJson[],
  /** 排泄 */
  defecations: DefecationJson[],
  /** ミルク */
  milks: MilkJson[],
  /** 食事 */
  meals: MealJson[],
  /** 薬 */
  drugs: DrugJson[],
};

/**
 * 健康記録入力
 */
export type HealthRecordInput = {
  /** コンテンツ */
  content: string,
  /** 時間 */
  time: string,
  /** Appかどうか */
  isApp: boolean,
  /** 別日かどうか */
  isOtherDay: boolean,
  /** 削除 */
  delete?: () => void,
}

const CustomListItem: React.FC<{
  name: string,
  icon?: JSX.Element,
  contents: HealthRecordInput[],
}> = ({ name, icon, contents }) => (
  <>
    <ListItem style={{ padding: '0px 0px 0px 10px' }}>
      <ListItemIcon style={{ minWidth: '100px' }}>
        {icon && <div style={{ marginRight: '8px' }}>{icon}</div>}
        {name}
      </ListItemIcon>
      <List style={{ padding: '5px', width: '100%' }}>
        {contents.map((content) => (
          <div key={content.time} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'contents' }}>
              <div
                style={{
                  display: 'inline-block',
                  marginRight: '10px',
                  color: 'gray',
                  minWidth: '70px',
                }}
              >
                <span>{content.time}</span>
              </div>
              <span style={{ color: (content.isOtherDay || !content.isApp) ? 'gray' : 'black' }}>{content.content}</span>
            </div>
            <div>
              {content.delete && (
                <Button
                  onClick={content.delete}
                  color="inherit"
                  disabled={!content.isApp}
                >
                  <DeleteIcon />
                </Button>
              )}
            </div>
          </div>
        ))}
      </List>
    </ListItem>
    <Divider />
  </>
);

/** プロパティ */
export type Props = {
  /** 日付 */
  date: string,
  /** データ */
  healthRecords: HealthRecords,
  /** 表示対象 */
  // eslint-disable-next-line react/no-unused-prop-types
  healthRecordTypes: HealthRecordTypeType[],
  /** 削除 */
  deleteFuncs?: {
    /** 体温 */
    temperature: (() => void)[],
    /** 睡眠 */
    sleep: (() => void)[],
    /** 排泄 */
    defecation: (() => void)[],
    /** ミルク */
    milk: (() => void)[],
    /** 食事 */
    meal: (() => void)[],
    /** 薬 */
    drug: (() => void)[],
  },
}

type deleteFuncType = {
  delete: () => void,
}

const HealthInfo: React.FC<Props> = ({
  date,
  healthRecords,
  // healthRecordTypes,
  deleteFuncs,
}) => {
  const [deleteFunc, setDeleteFunc] = React.useState<deleteFuncType | undefined>(undefined);
  // 健康記録
  const temperature: HealthRecordInput[] = healthRecords.temperatures.map((v, i) => {
    const dt = new Date(v.data.measureDateTime);
    const isOtherDay = getDateString(dt) !== date;
    return ({
      time: `${toStringTime(dt, false)}${isOtherDay ? '*' : ''}`,
      content: `${v.data.temperature}℃`,
      isApp: v.data.measurerType === 'app',
      isOtherDay,
      delete: deleteFuncs && (() => setDeleteFunc({ delete: deleteFuncs.temperature[i] })),
    });
  });
  const defecation: HealthRecordInput[] = healthRecords.defecations.map((v, i) => {
    const dt = new Date(v.data.measureDateTime);
    const isOtherDay = getDateString(dt) !== date;
    return ({
      time: `${toStringTime(dt, false)}${isOtherDay ? '*' : ''}`,
      content: `${v.data.defecationCategory}`,
      isApp: v.data.measurerType === 'app',
      isOtherDay,
      delete: deleteFuncs && (() => setDeleteFunc({ delete: deleteFuncs.defecation[i] })),
    });
  });
  const sleep: HealthRecordInput[] = [];
  healthRecords.sleeps.forEach((v, i) => {
    const dt = new Date(v.data.measureDateTime);
    const isOtherDay = getDateString(dt) !== date;
    const name = v.data.isSleepTime ? '就寝' : '起床';
    let content = '↓';
    if (
      !v.data.isSleepTime && healthRecords.sleeps[i - 1]
      && healthRecords.sleeps[i - 1].data.isSleepTime
    ) {
      // 起床時間に睡眠時間を記載
      const sleepTime = new Date(healthRecords.sleeps[i - 1].data.measureDateTime);
      content = diffHoursMinutes(sleepTime, dt);
    } else if (
      (v.data.isSleepTime && healthRecords.sleeps[i + 1]
        && healthRecords.sleeps[i + 1].data.isSleepTime)
    ) {
      // 記録不整合
      content = '※就寝が連続';
    } else if (
      !v.data.isSleepTime && !healthRecords.sleeps[i - 1]
    ) {
      // 記録不整合
      content = '※就寝未登録';
    } else if (
      !v.data.isSleepTime && !healthRecords.sleeps[i - 1].data.isSleepTime
    ) {
      // 記録不整合
      content = '※起床が連続';
    }
    sleep.push({
      time: `${toStringTime(new Date(v.data.measureDateTime), false)}${isOtherDay ? '*' : ' '}${name}`,
      content: `${content}`,
      isApp: v.data.measurerType === 'app',
      isOtherDay,
      delete: deleteFuncs && (() => setDeleteFunc({ delete: deleteFuncs.sleep[i] })),
    });
    if (v.data.isSleepTime && !healthRecords.sleeps[i + 1]) {
      // 就寝後記録なし
      const now = new Date();
      let content = '登録なし';
      if (now.getTime() - dt.getTime() < 24 * 60 * 60 * 1000) {
        content = diffHoursMinutes(dt, now);
      }
      sleep.push({
        time: '現在',
        content: `${content}`,
        isApp: false,
        isOtherDay: false,
        delete: deleteFuncs && (() => 0),
      });
    }
  });
  const milk: HealthRecordInput[] = healthRecords.milks.map((v, i) => {
    const dt = new Date(v.data.measureDateTime);
    const isOtherDay = getDateString(dt) !== date;
    return ({
      time: `${toStringTime(dt, false)}${isOtherDay ? '*' : ''}`,
      content: `${v.data.milk}ml`,
      isApp: v.data.measurerType === 'app',
      isOtherDay,
      delete: deleteFuncs && (() => setDeleteFunc({ delete: deleteFuncs.milk[i] })),
    });
  });
  const meal: HealthRecordInput[] = healthRecords.meals.map((v, i) => {
    const dt = new Date(v.data.measureDateTime);
    const isOtherDay = getDateString(dt) !== date;
    return ({
      time: `${toStringTime(dt, false)}${isOtherDay ? '*' : ''}`,
      content: `${v.data.mealContent}`,
      isApp: v.data.measurerType === 'app',
      isOtherDay,
      delete: deleteFuncs && (() => setDeleteFunc({ delete: deleteFuncs.meal[i] })),
    });
  });
  const drug: HealthRecordInput[] = healthRecords.drugs.map((v, i) => {
    const dt = new Date(v.data.measureDateTime);
    const isOtherDay = getDateString(dt) !== date;
    return ({
      time: `${toStringTime(dt, false)}${isOtherDay ? '*' : ''}`,
      content: `${v.data.drugContent}`,
      isApp: v.data.measurerType === 'app',
      isOtherDay,
      delete: deleteFuncs && (() => setDeleteFunc({ delete: deleteFuncs.drug[i] })),
    });
  });
  return (
    <>
      {/* {temperature && temperature.length > 0 && healthRecordTypes.includes('体温') && ( */}
      {temperature && temperature.length > 0 && (
        <CustomListItem name="体温" icon={<DeviceThermostatIcon />} contents={temperature} />
      )}
      {/* {sleep && sleep.length > 0 && healthRecordTypes.includes('睡眠') && ( */}
      {sleep && sleep.length > 0 && (
        <CustomListItem name="睡眠" icon={<SleepIcon />} contents={sleep} />
      )}
      {/* {defecation && defecation.length > 0 && healthRecordTypes.includes('排泄') && ( */}
      {defecation && defecation.length > 0 && (
        <CustomListItem name="排泄" icon={<BabyChangingStationIcon />} contents={defecation} />
      )}
      {/* {milk && milk.length > 0 && healthRecordTypes.includes('ミルク') && ( */}
      {milk && milk.length > 0 && (
        <CustomListItem name="ミルク" icon={<ChildCareIcon />} contents={milk} />
      )}
      {/* {meal && meal.length > 0 && healthRecordTypes.includes('食事') && ( */}
      {meal && meal.length > 0 && (
        <CustomListItem name="食事" icon={<RestaurantIcon />} contents={meal} />
      )}
      {/* {drug && drug.length > 0 && healthRecordTypes.includes('薬') && ( */}
      {drug && drug.length > 0 && (
        <CustomListItem name="薬" icon={<MedicationLiquidIcon />} contents={drug} />
      )}
      {deleteFunc && (
        <ConfirmDelete
          action={() => {
            deleteFunc.delete();
            setDeleteFunc(undefined);
          }}
          cancel={() => {
            setDeleteFunc(undefined);
          }}
        />
      )}
    </>
  );
};

export default HealthInfo;
